import * as PIXI from 'pixi.js';
import type { DisplayObjectEvents } from 'pixi.js';

import type {
    FetchResources,
    LifecycleAware,
    LuminaryObject,
    UpdatableLifecycleAware,
} from '../types';
import type { BufferingMode } from '../enums';
import type { RawVideoData } from '../video-data/types';
import type { ParentMixinSettings } from './parentMixin';
import { TypedParentMixin } from './parentMixin';
import type { LoadSettings, SerializableMixinSettings } from './serializableMixin';
import { TypedSerializableMixin } from './serializableMixin';
import type { UpdateFlowMixinSettings } from './updateFlowMixin';
import { TypedUpdateFlowMixin } from './updateFlowMixin';

export type BaseSettings = SerializableMixinSettings &
    ParentMixinSettings &
    UpdateFlowMixinSettings & {
        fetchResources: FetchResources;
    };

/**
 * Base class, groups the use our `update` and `serializable` mixin and attaches an `eventEmitter`. This class should not
 * be used on its own. We should only inherit from it and define the necessary `checkAndFix` and `update` logic there.
 *
 */
export abstract class TimeAwareBase
    extends TypedUpdateFlowMixin(
        TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter<DisplayObjectEvents>)),
    )
    implements LuminaryObject, UpdatableLifecycleAware
{
    override _classType = 'Base';

    /**
     * Determine if the object has been destroyed via destroy(). If true, it should not be used.
     */
    #destroyed = false;

    readonly #fetchResources: FetchResources = true;

    constructor(settings: BaseSettings) {
        super(settings);
        this.#fetchResources = settings.fetchResources;

        const onAssetsCanPlay = () => {
            if (this.canPlay) {
                this._emitAssetsCanPlay();
            }
        };
        this.on('assetsCanPlay', onAssetsCanPlay); // Listen to luminary objects bubbling the event
        this.on('assetCanPlay', onAssetsCanPlay); // Listen to assets on this object
    }

    /**
     * Called while seeking, forwarding a request to buffer any data (if need be) down the hierarchy.
     */
    abstract _propagateBuffering(bufferingMode: BufferingMode): Promise<void>;

    abstract _propagatePlay(): void;

    abstract _emitAssetsCanPlay(): void;

    /**
     * Propagate pause to all lifecycle-aware objects.
     *
     * @internal
     */
    _propagatePause(): void {
        this._getLifecycleAwareObjects().forEach((obj) => obj._propagatePause());
    }

    /**
     * Get all lifecycle-aware objects that participate in lifecycle events (canPlay, isLoaded, etc.).
     * Override this in subclasses to return the objects that should be aggregated.
     *
     * @internal
     */
    protected _getLifecycleAwareObjects(): LifecycleAware[] {
        return [];
    }

    /**
     * Returns true if all lifecycle-aware objects have finished loading or failed to load.
     * If any objects are still loading or retrying, this will be false.
     */
    get isDoneTryingToLoad(): boolean {
        return this._getLifecycleAwareObjects().every((obj) => obj.isDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     * @deprecated Use {@link isDoneTryingToLoad} instead.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this.isDoneTryingToLoad;
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    get isLoaded(): boolean {
        return this._getLifecycleAwareObjects().every((obj) => obj.isLoaded);
    }

    /**
     * Returns true if all assets (deeply) within this object can be played back.
     */
    get canPlay(): boolean {
        return this._getLifecycleAwareObjects().every((obj) => obj.canPlay);
    }

    get destroyed() {
        return this.#destroyed;
    }

    override get parent(): TimeAwareBase {
        return super.parent as TimeAwareBase;
    }

    /**
     * Flag to indicate if resource fetching is enabled. Note this cannot be changed
     * after instantiation.
     */
    get fetchResources(): FetchResources {
        return this.#fetchResources;
    }

    /**
     * Bulk load properties with autoUpdate disabled. See
     * {@link Luminary.SerializableMixin#load} for more details on load.
     */
    override load(
        // TODO(strict-ts): Change to Record<string, unknown> when serializableMixin is updated
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataObj: RawVideoData | Record<string, any>,
        settings: LoadSettings = { skipTemplateAttributes: false },
    ) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        super.load(dataObj, settings);

        this.autoUpdate = initAutoUpdate;
    }

    /**
     * Base destroy method. This will automatically remove all current event listeners. Do not use a Base object after
     * calling `destroy()`.
     */
    destroy(_args?: unknown) {
        this.#destroyed = true;
        this.removeAllListeners();
    }

    /**
     * Base clear method. Clear should reset the internal data to an initial state.
     */
    clear() {}

    override get readonlyProperties(): string[] {
        // Most Base objects don't serialize their readonly Properties
        return [];
    }
}

/**
 * Testing that mixins are properly implemented:
 *
 * Uncomment this class and see if only the indicated line shows an error.
 *
 * class Test extends Base { // No error
 *     constructor() { // No error
 *         super({}); // No error.
 *         this.emit('test'); // No error: defined in PIXI.EventEmitter
 *         this.parent; // No error: is defined in ParentMixin
 *         this.bingbong(); // Shows an error: method does not exist.
 *     }
 * }
 *
 */
